import React, { useState, useRef, useEffect } from 'react';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { format } from 'date-fns';
import { Trash2 } from 'lucide-react';
import ProjectSelect from './ProjectSelect';

const ManageTimeForm = ({
  date, setDate,
  calOpen, setCalOpen,
  selectedEmployee, setSelectedEmployee,
  vehicleExpense, setVehicleExpense,
  fuelExpense, setFuelExpense,
  timeCards, setTimeCards,
  editingSlipId,
  employees,
  projectOptions,
  onAddProject,
  onSaveTime,
}) => {
  // Free-typing employee combobox state.
  const [empOpen, setEmpOpen] = useState(false);
  const empWrapperRef = useRef(null);
  useEffect(() => {
    const onDocClick = (e) => {
      if (empWrapperRef.current && !empWrapperRef.current.contains(e.target)) {
        setEmpOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);
  const empQuery = (selectedEmployee || '').toLowerCase().trim();
  // Allow multi-token search: every space-separated token must appear somewhere
  // in the name (so "barber jameel", "jameel barber", "jameel", or "barb"
  // all work).
  const empTokens = empQuery.split(/\s+/).filter(Boolean);
  const empMatches = employees
    .map((e) => ({ e, name: `${e.last_name}, ${e.first_name}` }))
    .filter(({ name }) => {
      if (empTokens.length === 0) return true;
      const lower = name.toLowerCase();
      return empTokens.every((tok) => lower.includes(tok));
    })
    .slice(0, 50);

  return (
    <div className="bg-white rounded shadow p-6 min-w-0">
      <h3 className="text-2xl text-[#5B8DB8] font-light mb-6">Manage Time</h3>

      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <label className="text-red-500 text-sm w-32 text-right">Date:</label>
          <Popover open={calOpen} onOpenChange={setCalOpen}>
            <PopoverTrigger asChild>
              <input
                type="text"
                value={date}
                readOnly
                onClick={() => setCalOpen(true)}
                className="border border-gray-300 rounded px-3 py-2 w-28 cursor-pointer focus:outline-none focus:border-[#6BADE0] focus:ring-1 focus:ring-[#6BADE0]"
                data-testid="date-input"
              />
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={date ? new Date(date) : undefined}
                onSelect={(d) => { if (d) setDate(format(d, 'M/dd/yyyy')); setCalOpen(false); }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex items-center gap-4">
          <label className="text-red-500 text-sm w-32 text-right">Employee:</label>
          <div ref={empWrapperRef} className="flex-1 relative">
            <input
              type="text"
              value={selectedEmployee}
              onChange={(e) => { setSelectedEmployee(e.target.value); setEmpOpen(true); }}
              onFocus={() => setEmpOpen(true)}
              placeholder="Type an employee name..."
              className="border border-gray-300 rounded px-3 py-2 w-full"
              data-testid="employee-select"
              autoComplete="off"
            />
            {empOpen && empMatches.length > 0 && (
              <ul
                className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto bg-white border border-gray-300 rounded shadow-lg"
                data-testid="employee-options-list"
              >
                {empMatches.map(({ e, name }, idx) => (
                  <li
                    key={`emp-${idx}-${name}`}
                    onMouseDown={(ev) => {
                      // mousedown so blur doesn't fire before we set the value.
                      ev.preventDefault();
                      setSelectedEmployee(name);
                      setEmpOpen(false);
                    }}
                    className="px-3 py-2 text-sm text-gray-700 cursor-pointer hover:bg-[#5B9BD5] hover:text-white"
                    data-testid={`employee-option-${idx}`}
                  >
                    {name}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="flex items-center gap-4">
          <label className="text-sm w-32 text-right">Vehicle Expense:</label>
          <input
            type="text"
            value={vehicleExpense}
            onChange={(e) => setVehicleExpense(e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 w-24"
            data-testid="vehicle-expense-input"
          />
        </div>

        <div className="flex items-center gap-4">
          <label className="text-sm w-32 text-right">Fuel Expense ($):</label>
          <input
            type="text"
            value={fuelExpense}
            onChange={(e) => setFuelExpense(e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 w-24"
            data-testid="fuel-expense-input"
          />
        </div>

        {/* Time Card rows */}
        <div>
          <div className="flex items-end gap-4 mb-1">
            <div className="w-32"></div>
            <div className="flex-1 text-sm text-gray-700 font-medium pl-1">Project</div>
            <div className="text-sm text-gray-700 font-medium w-20 text-center">Hours</div>
          </div>
          {timeCards.map((tc, idx) => (
            <div key={idx} className="flex items-start gap-4 mb-2">
              <label className="text-red-500 text-sm w-32 text-right mt-2">Time Card</label>
              <ProjectSelect
                value={tc.project}
                onChange={(val) => setTimeCards(prev => prev.map((c, i) => i === idx ? { ...c, project: val } : c))}
                options={projectOptions}
              />
              <input
                type="text"
                value={tc.hours}
                onChange={(e) => setTimeCards(prev => prev.map((c, i) => i === idx ? { ...c, hours: e.target.value } : c))}
                className="border border-gray-300 rounded px-3 py-2 w-20"
                data-testid={`hours-input-${idx}`}
              />
              {idx > 0 && (
                <button
                  onClick={() => setTimeCards(prev => prev.filter((_, i) => i !== idx))}
                  className="bg-[#DC3545] hover:bg-[#C82333] text-white rounded p-2 mt-0.5"
                  data-testid={`delete-timecard-${idx}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="flex justify-end">
          <button
            onClick={onAddProject}
            className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white px-4 py-1.5 rounded transition-colors text-sm"
            data-testid="add-project-btn"
          >
            + Add Project
          </button>
        </div>

        <div className="flex justify-center">
          <button
            onClick={onSaveTime}
            data-editing-id={editingSlipId || ''}
            className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white px-8 py-3 rounded text-lg font-medium transition-colors"
            data-testid="save-time-btn"
          >
            {editingSlipId ? 'Update Slip' : 'Save Time'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManageTimeForm;
